import { HeaderNavigation } from './types';
import { Navigation } from './Navigation';

export class DesktopNavigation extends Navigation implements HeaderNavigation {
  subnavigationLinks?: NodeListOf<HTMLLinkElement>;
  flyouts?: NodeListOf<HTMLDivElement>;

  /**
   *
   * @param headerEl      The <header /> element the navigation is part of
   * @param navigationEl  The <nav /> element containing the links we have to manage
   */
  constructor(headerEl: HTMLElement, navigationEl: HTMLElement) {
    super(headerEl, navigationEl);

    //
    this.links = this.navigationEl.querySelectorAll(
      'a.header__navigation-link[data-target]',
    );

    this.subnavigationLinks = this.headerEl.querySelectorAll(
      '.header__flyout a.header__navigation-link[data-subnavigation]',
    );

    this.flyouts = this.headerEl.querySelectorAll('.header__flyout');

    // bind handlers to this
    this.handleNavigationLinkClick = this.handleNavigationLinkClick.bind(this);
    this.handleSubNavigationClick = this.handleSubNavigationClick.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);

    // add event listeners
    this.addEventListeners();
  }

  /**
   *
   * @protected
   */
  protected addEventListeners() {
    this.links?.forEach((link) => {
      (link as HTMLLinkElement).addEventListener(
        'click',
        this.handleNavigationLinkClick,
      );
    });

    this.subnavigationLinks?.forEach((link) => {
      (link as HTMLLinkElement).addEventListener(
        'click',
        this.handleSubNavigationClick,
      );
    });

    document.addEventListener('click', this.handleOutsideClick);

    //Close Flyout on ESCAPE on desktop
    document.addEventListener('keydown', (e) => {
      if (
        e.key == 'Escape' &&
        !this.headerEl.classList.contains('header--mobile-navigation-open')
      ) {
        const activeFlyouts = this.headerEl.querySelectorAll(
          '.header__flyout.header__flyout--active',
        );

        // close them if the click was outside the header
        for (const flyout of activeFlyouts) {
          this.closeFlyout(flyout as HTMLElement);
        }
      }
    });
  }

  /**
   *
   * @protected
   */
  protected removeEventListeners() {
    this.links?.forEach((link) => {
      (link as HTMLLinkElement).removeEventListener(
        'click',
        this.handleNavigationLinkClick,
      );
    });

    this.subnavigationLinks?.forEach((link) => {
      (link as HTMLLinkElement).removeEventListener(
        'click',
        this.handleSubNavigationClick,
      );
    });

    document.removeEventListener('click', this.handleOutsideClick);
  }

  /**
   * Open the flyout for the given main navigation link
   *
   * @param link
   */
  openFlyout(link: HTMLLinkElement) {
    // try to get the id of the flyout we must open
    const flyoutId = link.getAttribute('data-target');

    if (!flyoutId) return;

    // query the flyout by id
    const flyoutEl = this.headerEl.querySelector(flyoutId) as HTMLElement;

    if (!flyoutEl) return;

    // close all currently opened flyout's
    const flyouts = this.headerEl.querySelectorAll(
      '.header__flyout.header__flyout--active',
    );
    for (const flyout of flyouts) {
      if (flyout !== flyoutEl) {
        (flyout as HTMLElement).classList.remove('header__flyout--active');
      }
    }

    // console.log(flyoutEl);

    // set the flyout as active eg. open it
    flyoutEl.classList.toggle('header__flyout--active');

    // update the header state based on whether a flyout is active or not
    if (flyoutEl.classList.contains('header__flyout--active')) {
      this.headerEl.classList.add('header--navigation-open');
    } else {
      this.headerEl.classList.remove('header--navigation-open');
    }

    // remove focus class if link has focus class
    if (link.classList.contains('header__navigation-link--focus')) {
      link.classList.remove('header__navigation-link--focus');
      return;
    }

    // remove focus class of all links
    this.headerEl
      .querySelectorAll('.header__navigation-link')
      .forEach((element) => {
        if (element.classList.contains('header__navigation-link--focus')) {
          element.classList.remove('header__navigation-link--focus');
        }
      });

    // set focus class of clicked link
    link.classList.add('header__navigation-link--focus');
  }

  /**
   * Close the given flyout
   *
   * @param flyoutEl
   */
  closeFlyout(flyoutEl: HTMLElement) {
    console.log('foo');
    // set the flyout as active eg. open it
    flyoutEl.classList.remove('header__flyout--active');

    // remove the active state from the active navigation item (li)
    const activeItems = this.headerEl.querySelectorAll(
      '.header__navigation-item--active',
    );
    for (const item of activeItems) {
      (item as HTMLLIElement).classList.remove(
        'header__navigation-item--active',
      );
    }

    // remove the active state from previously activated subnavigations
    const activeSubnavigations = this.headerEl.querySelectorAll(
      '.header__subnavigation--active',
    );
    for (const navi of activeSubnavigations) {
      (navi as HTMLElement).classList.remove('header__subnavigation--active');
    }

    // remove the header state
    this.headerEl.classList.remove('header--navigation-open');

    // remove focus class
    this.headerEl
      .querySelectorAll('.header__navigation-link')
      .forEach((element) => {
        if (element.classList.contains('header__navigation-link--focus')) {
          element.classList.remove('header__navigation-link--focus');
        }
      });
  }

  /**
   * Open the subnavigation of the given navigation item. Mainly this will
   * close all already opened subnavigations, hide the current active links
   * and open the linked subnavigation of the given navigation-item.
   *
   * @param item  The li.header__navigation-item element to open the subnavigation for.
   */
  openSubnavigation(item: HTMLElement) {
    // get the ul- element of the clicked navigation item (li)
    const container = item.closest('ul.header__navigation-items');
    // query the navigation items of the ul- element
    const items = container?.querySelectorAll('& > .header__navigation-item');
    // query the link of the clicked navigation item
    const link = item?.querySelector(
      'a.header__navigation-link',
    ) as HTMLLinkElement;
    // query the flyout the clicked item is located in
    const flyout = link.closest('.header__flyout');
    // get the id of the subnavigation the link should open
    const subnavigationId = link.getAttribute('data-subnavigation');

    if (!subnavigationId) return;

    const subnavigation = this.headerEl.querySelector(subnavigationId);

    if (!container || !link || !flyout || !subnavigation || !items) return;

    // Determine the current level based on the parent subnavigation
    const parentSubnav = container.closest('.header__subnavigation');
    let currentLevel = 1;
    if (parentSubnav?.classList.contains('header__subnavigation--level2')) {
      currentLevel = 2;
    } else if (parentSubnav?.classList.contains('header__subnavigation--level3')) {
      currentLevel = 3;
    }

    // set all other navigation items (li) as not active
    for (const i of items) {
      i.classList.remove('header__navigation-item--active');
    }
    // set the item with the clicked link as active
    item.classList.add('header__navigation-item--active');

    // Close all subnavigations at the same level and deeper levels
    const nextLevel = currentLevel + 1;
    for (let level = nextLevel; level <= 3; level++) {
      const levelClass = `header__subnavigation--level${level}`;
      const subnavigations = flyout?.querySelectorAll(`.${levelClass}`);
      subnavigations?.forEach((navi) => {
        (navi as HTMLElement).classList.remove('header__subnavigation--active');
      });
    }

    // activate eg. show the desired one
    subnavigation.classList.add('header__subnavigation--active');
  }

  /**
   * Handle the clicks on the main navigation items in the header
   *
   * @param e
   */
  handleNavigationLinkClick(e: MouseEvent) {
    e.preventDefault();

    // get the clicked link
    const link = (e.target as HTMLElement).closest(
      '.header__navigation-link',
    ) as HTMLLinkElement;

    // open the flyout
    this.openFlyout(link);
  }

  /**
   * Handle the clicks on the links in the subnavigations of our flyouts
   *
   * @param e
   */
  handleSubNavigationClick(e: MouseEvent) {
    e.preventDefault();

    // get hte navigation item of the clicked link
    const item = (e.target as HTMLElement).closest(
      '.header__navigation-item',
    ) as HTMLLinkElement;

    // open the subnavigation
    this.openSubnavigation(item);
  }

  /**
   * Handle clicks outside the flyouts to close the subnavigations
   * @param e
   */
  handleOutsideClick(e: MouseEvent) {
    // get all active flyouts
    const activeFlyouts = this.headerEl.querySelectorAll(
      '.header__flyout.header__flyout--active',
    );

    // close them if the click was outside the header
    for (const flyout of activeFlyouts) {
      if (!this.headerEl.contains(e.target as Node)) {
        this.closeFlyout(flyout as HTMLElement);
      }
    }
  }
}
